import { Col, Row, Image, Menu, Collapse, Tooltip } from 'antd';
import { PlusCircleOutlined, MinusCircleOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import Link from 'next/link';
import { useRouter } from "next/router";
import { useState, useEffect, useMemo, useRef } from 'react';
import { useSession } from "next-auth/react";
import UserProfileModal from "../pages/components/UserProfileModal";
import HelpFAQModal from "./HelpFAQModal";
import ComingSoonModal from "./ComingSoonModal";
import Avatar from "react-avatar";
import Modal from 'react-modal';
import styles from '../styles/UserProfile.module.css';
import { useTheme } from "next-themes";
import collapsibleStyles from '../styles/SidebarCollapsible.module.css';

export default function Sidebar({ sideMenuArrProp }) {
  const SIDEBAR_SET_COLLAPSED_EVENT = 'app:sidebar:set-collapsed';
  const SIDEBAR_COLLAPSED_CHANGED_EVENT = 'app:sidebar:collapsed-changed';
  const router = useRouter();
  const { data: session } = useSession();
  const [sideMenuArr, setSideMenuArr] = useState(() => sideMenuArrProp ?? []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalHelp, setIsModalHelp] = useState(false);
  const [isFAQOpen, setIsFAQOpen] = useState(false);
  const [comingSoonTitle, setComingSoonTitle] = useState('');
  const { theme } = useTheme();
  const [fctLogo, setFctLogo] = useState('/images/fctlogo1.png');
  const mediaQueryRef = useRef(null);
  const sidebarRef = useRef(null);
  const flyoutCloseTimerRef = useRef(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openFlyoutGroup, setOpenFlyoutGroup] = useState(null);
  const { Panel } = Collapse;

  useEffect(() => {
    if (sideMenuArrProp && sideMenuArrProp.length > 0) {
      setSideMenuArr(sideMenuArrProp);
      return;
    }
    const stored = localStorage.getItem("menubardata");
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      const activeModule = router.pathname.split("/")[1];
      const matchedModule = parsed?.dashboard?.find((d) => d.URL && d.URL.split("/")[1] === activeModule);
      if (matchedModule?.sideMenu) {
        setSideMenuArr(matchedModule.sideMenu);
      } else {
        const allMenus = parsed?.dashboard?.flatMap(d => d.sideMenu) || [];
        setSideMenuArr(allMenus);
      }
    } catch (err) {
      console.error("Sidebar parse error:", err);
    }
  }, [sideMenuArrProp, router.pathname]);

  const { groupedMenu, ungroupedMenu, dashboardItem } = useMemo(() => {
    const grouped = {};
    const ungrouped = [];
    let dashboardItem = null;

    sideMenuArr.forEach(item => {
      if (item.Group) {
        if (!grouped[item.Group]) grouped[item.Group] = [];
        grouped[item.Group].push(item);
      } else if (item.Name === "Dashboard") {
        dashboardItem = item;
      } else {
        ungrouped.push(item);
      }
    });

    return { groupedMenu: grouped, ungroupedMenu: ungrouped, dashboardItem };
  }, [sideMenuArr]);

  useEffect(() => {
    let isMounted = true;
    mediaQueryRef.current = window.matchMedia('(prefers-color-scheme: dark)');
    const prefersDarkMode = mediaQueryRef.current.matches;
    if (isMounted) {
      setFctLogo(theme === 'dark' || prefersDarkMode ? '/images/fctlogo1.png' : '/images/fc-logo-dark.png');
    }
    return () => {
      isMounted = false;
      mediaQueryRef.current = null;
    };
  }, [theme])
  const helpItemStyle = {
    display: 'flex', alignItems: 'center', gap: '12px',
    backgroundColor: '#f0f4f8', borderRadius: '8px',
    padding: '10px 12px', marginBottom: '10px',
    textDecoration: 'none', color: '#1e293b',
    transition: 'background-color 0.2s ease',
  };
  const futureItemStyle = { opacity: 0.55, cursor: 'not-allowed' };
  const helpIconStyle = { fontSize: '20px', flexShrink: 0 };
  const badgeStyle = {
    marginLeft: '8px', fontSize: '10px',
    backgroundColor: '#e0f0ff', color: '#0c6fad',
    borderRadius: '4px', padding: '2px 6px',
    fontWeight: '500', verticalAlign: 'middle',
  };

  useEffect(() => {
    const sidebarWrapper = sidebarRef.current?.parentElement;
    if (!sidebarWrapper) return;
    const currentSidebarWidth = isCollapsed ? '70px' : '260px';
    sidebarWrapper.style.width = currentSidebarWidth;
    sidebarWrapper.style.transition = 'width 0.25s ease';
    document.documentElement.style.setProperty('--app-sidebar-width', currentSidebarWidth);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(SIDEBAR_COLLAPSED_CHANGED_EVENT, {
          detail: { collapsed: isCollapsed },
        })
      );
    }
    if (isCollapsed) {
      setOpenFlyoutGroup(null);
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleSetCollapsed = (event) => {
      const nextCollapsed = event?.detail?.collapsed;
      if (typeof nextCollapsed === 'boolean') {
        setIsCollapsed(nextCollapsed);
      }
    };
    window.addEventListener(SIDEBAR_SET_COLLAPSED_EVENT, handleSetCollapsed);
    return () => {
      window.removeEventListener(SIDEBAR_SET_COLLAPSED_EVENT, handleSetCollapsed);
    };
  }, []);

  useEffect(() => {
    const currentSidebarRef = sidebarRef.current;
    const currentFlyoutCloseTimerRef = flyoutCloseTimerRef.current;
    return () => {
      if (currentFlyoutCloseTimerRef) {
        clearTimeout(currentFlyoutCloseTimerRef);
      }
      const sidebarWrapper = currentSidebarRef?.parentElement;
      if (sidebarWrapper) {
        sidebarWrapper.style.width = '';
        sidebarWrapper.style.transition = '';
      }
      document.documentElement.style.removeProperty('--app-sidebar-width');
    };
  }, []);

  const openModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const openHelpModal = () => {
    setIsModalHelp(true);

  };
  const closeHelpModal = () => {
    setIsModalHelp(false);
  };
  const userNameStyle = {
    color: '#1e293b',
    fontWeight: 'bold'
  };

  const selected = {
    color: '#1e3a8a'
  }

  const unSelected = {
    color: '#1e293b'
  }

  const openGroupFlyout = (groupName) => {
    if (flyoutCloseTimerRef.current) {
      clearTimeout(flyoutCloseTimerRef.current);
    }
    setOpenFlyoutGroup(groupName);
  };

  const scheduleFlyoutClose = () => {
    if (flyoutCloseTimerRef.current) {
      clearTimeout(flyoutCloseTimerRef.current);
    }
    flyoutCloseTimerRef.current = setTimeout(() => {
      setOpenFlyoutGroup(null);
    }, 120);
  };

  const keepFlyoutOpen = () => {
    if (flyoutCloseTimerRef.current) {
      clearTimeout(flyoutCloseTimerRef.current);
    }
  };

  // useEffect(() => {
  //   if (!session) {
  //     router.push("/");
  //   }
  // }, [session]);


  const renderMenuItem = (item, index) => {
    const isActive = router.pathname === item.URL;
    return (
      <li key={index} className={isActive ? 'active' : ''}>
        <Link legacyBehavior href={item.URL}>
          <a style={isActive ? selected : unSelected}>
            <Image src={item.path} className="img-active" preview={false} style={{ marginRight: '8px' }} alt="" />
            <Image src={item.path} className="img-default" preview={false} style={{ marginRight: '8px' }} alt="" />
            <span>{item.Name}</span>
          </a>
        </Link>
      </li>
    );
  };

  return (
    <>
      <div
        ref={sidebarRef}
        className={`main-sidebar ${collapsibleStyles.sidebarContainer} ${isCollapsed ? collapsibleStyles.collapsedSidebar : collapsibleStyles.expandedSidebar}`}
      >
        <Row align='middle' justify={isCollapsed ? 'center' : 'space-between'} className='sidebar-logo' style={{ padding: '0 12px', minHeight: 64, height: 64 }}>
          {!isCollapsed && (
            <Col>
              <Image
                src='/images/fctlogo1.png'
                alt="Logo" height={45} preview={false} />
            </Col>
          )}
          <Col>
            <Tooltip title={isCollapsed ? 'Expand' : null} placement="right">
              <button
                type="button"
                className={collapsibleStyles.collapseToggle}
                aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                onClick={() => setIsCollapsed((prev) => !prev)}
              >
                {isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              </button>
            </Tooltip>
          </Col>
        </Row>
        <div className={`sidebar-list ${collapsibleStyles.menuContainer}`}>
          {!isCollapsed && (
            <>
              {dashboardItem && (
                <ul className="sidebar-list">
                  {renderMenuItem(dashboardItem, 'dashboard')}
                </ul>
              )}
              <Collapse accordion bordered={false} expandIcon={({ isActive }) =>
                isActive ? <MinusCircleOutlined /> : <PlusCircleOutlined />
              }>
                {Object.entries(groupedMenu).map(([groupName, items]) => {
                  const isGroupActive = items.some(item => router.pathname === item.URL);
                  return (
                    <Panel header={groupName} className={isGroupActive ? 'active-group' : ''} key={groupName}>
                      <ul className="sidebar-list">
                        {items.map(renderMenuItem)}
                      </ul>
                    </Panel>
                  );
                })}
              </Collapse>
              <ul className="sidebar-list">
                {ungroupedMenu.map(renderMenuItem)}
              </ul>
            </>
          )}

          {isCollapsed && (
            <ul className={`sidebar-list ${collapsibleStyles.collapsedIconList}`}>
              {dashboardItem && (() => {
                const isActive = router.pathname === dashboardItem.URL;
                return (
                  <li key="dashboard" className={`${isActive ? 'active' : ''} ${collapsibleStyles.collapsedIconItem}`}>
                    <Link legacyBehavior href={dashboardItem.URL}>
                      <a style={isActive ? selected : unSelected} className={collapsibleStyles.iconOnlyLink}>
                        <Tooltip placement="right" title={dashboardItem.Name} overlayClassName={collapsibleStyles.sidebarTooltip}>
                          <Image src={dashboardItem.path} className="img-active" preview={false} alt="" />
                          <Image src={dashboardItem.path} className="img-default" preview={false} alt="" />
                        </Tooltip>
                      </a>
                    </Link>
                  </li>
                );
              })()}
              {Object.entries(groupedMenu).map(([groupName, items]) => {
                const isGroupActive = items.some(item => router.pathname === item.URL);
                const groupIcon = items?.[0]?.path;
                return (
                  <li
                    key={groupName}
                    className={`${isGroupActive ? 'active' : ''} ${collapsibleStyles.collapsedIconItem}`}
                    onMouseEnter={() => openGroupFlyout(groupName)}
                    onMouseLeave={scheduleFlyoutClose}
                  >
                    <div className={collapsibleStyles.iconOnlyLink}>
                      {groupIcon && (
                        <>
                          <Image src={groupIcon} className="img-active" preview={false} alt="" />
                          <Image src={groupIcon} className="img-default" preview={false} alt="" />
                        </>
                      )}
                    </div>
                    {openFlyoutGroup === groupName && (
                      <div
                        className={collapsibleStyles.flyoutPanel}
                        onMouseEnter={keepFlyoutOpen}
                        onMouseLeave={scheduleFlyoutClose}
                      >
                        <div className={collapsibleStyles.flyoutHeader}>{groupName}</div>
                        <ul className={collapsibleStyles.flyoutList}>
                          {items.map((item, index) => {
                            const isActive = router.pathname === item.URL;
                            const linkStyle = isActive ? selected : unSelected;
                            return (
                              <li key={`${groupName}-${index}`} className={isActive ? collapsibleStyles.flyoutActive : ''}>
                                <Link legacyBehavior href={item.URL}>
                                  <a style={linkStyle}>
                                    <Image src={item.path} preview={false} alt="" />
                                    <span>{item.Name}</span>
                                  </a>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}

              {ungroupedMenu.map((item, index) => {
                const isActive = router.pathname === item.URL;
                const linkStyle = isActive ? selected : unSelected;
                return (
                  <li key={`ungrouped-${index}`} className={`${isActive ? 'active' : ''} ${collapsibleStyles.collapsedIconItem}`}>
                    <Link legacyBehavior href={item.URL}>
                      <a style={linkStyle} className={collapsibleStyles.iconOnlyLink}>
                        <Tooltip placement="right" title={item.Name} overlayClassName={collapsibleStyles.sidebarTooltip}>
                          <Image src={item.path} className="img-active" preview={false} alt="" />
                          <Image src={item.path} className="img-default" preview={false} alt="" />
                        </Tooltip>
                      </a>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className='sidebarBottom'>
          <Row>
            <Col span={24}>
              <ul className="sidebar-list pb-10">
                <>
                  <Avatar name='?' size="30" onClick={() => setIsModalHelp(true)} title='Help' round={true} color="rgb(12,63,107)" className='userProfile' />
                  {!isCollapsed && <span style={userNameStyle}>Help & Support</span>}
                  {isModalHelp && (
                    <Modal
                      isOpen={isModalHelp}
                      onRequestClose={closeHelpModal}
                      contentLabel="Application Version"
                      style={{
                        overlay: {
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          zIndex: 5000,
                        }
                      }}
                      className={styles.userProfileModalPopup}
                    >
                      <button className={styles.onRequestClose} onClick={() => setIsModalHelp(false)}>
                        <span>&times;</span>
                      </button>
                      <div style={{ minWidth: '260px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                          <span style={{ fontSize: '20px' }}>❓</span>
                          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>Help &amp; Support</h3>
                        </div>

                        {session?.user?.roles?.[0] !== 'Admin' && (
                          <>
                            <div style={{ ...helpItemStyle, cursor: 'pointer' }}
                              onClick={() => { setIsModalHelp(false); router.push('/help/user-guide'); }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#d6eaf8'}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f0f4f8'}>
                              <span style={helpIconStyle}>📘</span>
                              <div>
                                <div style={{ fontWeight: '600', fontSize: '14px' }}>User Guides</div>
                                <div style={{ fontSize: '12px', color: '#667085' }}>Documentation &amp; manuals</div>
                              </div>
                            </div>
                            <div style={{ ...helpItemStyle, cursor: 'pointer' }} onClick={() => { setIsFAQOpen(true); setIsModalHelp(false); }}>
                              <span style={helpIconStyle}>💬</span>
                              <div>
                                <div style={{ fontWeight: '600', fontSize: '14px' }}>FAQs</div>
                                <div style={{ fontSize: '12px', color: '#667085' }}>Frequently asked questions</div>
                              </div>
                            </div>
                            <div style={{ ...helpItemStyle, ...futureItemStyle }} onClick={() => { setComingSoonTitle('Raise a Ticket'); setIsModalHelp(false); }}>
                              <span style={helpIconStyle}>🎫</span>
                              <div>
                                <div style={{ fontWeight: '600', fontSize: '14px' }}>
                                  Raise a Ticket <span style={badgeStyle}>Coming Soon</span>
                                </div>
                                <div style={{ fontSize: '12px', color: '#667085' }}>EVA integration</div>
                              </div>
                            </div>
                            {session?.user?.roles?.[0] !== 'Internal Manager' && session?.user?.roles?.[0] !== 'Internal Admin' && (
                              <div style={{ ...helpItemStyle, cursor: 'pointer' }} onClick={(e) => {
                                e.stopPropagation();
                                const name = localStorage.name || '';
                                const to = 'HRSupport@fourcolorstech.com'.trim();
                                const subject = encodeURIComponent('Support Request');
                                const body = encodeURIComponent(`Hello,\n\n\nThanks & Regards,\n${name}`);
                                const a = document.createElement('a');
                                a.href = `https://outlook.cloud.microsoft/mail/deeplink/compose?to=${to}&subject=${subject}&body=${body}`;
                                a.target = '_blank';
                                a.rel = 'noopener noreferrer';
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                setIsModalHelp(false);
                              }}>
                                <span style={helpIconStyle}>📩</span>
                                <div>
                                  <div style={{ fontWeight: '600', fontSize: '14px' }}>Contact Admin</div>
                                  <div style={{ fontSize: '12px', color: '#667085' }}>Send mail to HR Support</div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        {session?.user?.roles?.[0] === 'Admin' && (
                          <>
                            <div style={{ ...helpItemStyle, cursor: 'pointer' }} onClick={() => { setIsModalHelp(false); }}>
                              <span style={helpIconStyle}>📢</span>
                              <div>
                                <div style={{ fontWeight: '600', fontSize: '14px' }}>System Announcements</div>
                                <div style={{ fontSize: '12px', color: '#667085' }}>Manage announcements</div>
                              </div>
                            </div>
                            <div style={{ ...helpItemStyle, cursor: 'pointer' }} onClick={() => { setIsModalHelp(false); }}>
                              <span style={helpIconStyle}>⚙️</span>
                              <div>
                                <div style={{ fontWeight: '600', fontSize: '14px' }}>Support Configuration</div>
                                <div style={{ fontSize: '12px', color: '#667085' }}>Configure support settings</div>
                              </div>
                            </div>
                          </>
                        )}

                        <div style={{ borderTop: '1px solid #ddd', marginTop: '14px', paddingTop: '10px', textAlign: 'center' }}>
                          <div className={styles.PopUpLogo}>
                            <Image src={fctLogo} alt="Logo" height={22} preview={false} />
                          </div>
                          <p style={{ fontSize: '11px', color: '#999', margin: '6px 0 0' }}>EDX Version 2410</p>
                        </div>
                      </div>
                    </Modal>
                  )}
                </>
              </ul>
            </Col>
            <br />
            <br />
            <Col span={24}>
              <ul className="sidebar-list pb-10">
                <>
                  <Avatar
                    name={localStorage.name ? localStorage.name : ""}
                    size="30" onClick={() => setIsModalOpen(true)} round={true}
                    color="rgb(12,63,107)" className='userProfile'
                  />
                  {!isCollapsed && <span style={userNameStyle}>{localStorage.name ? localStorage.name : ""}</span>}
                  <UserProfileModal
                    isOpen={isModalOpen}
                    onRequestClose={closeModal}
                  />
                </>
              </ul>
            </Col>
          </Row>
        </div>
      </div>
      <HelpFAQModal isOpen={isFAQOpen} onClose={() => setIsFAQOpen(false)} />
      <ComingSoonModal isOpen={!!comingSoonTitle} onClose={() => setComingSoonTitle('')} title={comingSoonTitle} />
    </>
  );
}
